import moment from 'moment/moment'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { BaseRecord } from '../../models/BaseRecord'
import { Estimate } from '../../models/Estimate'
import { FEATURE, Feature } from '../../models/Feature'
import { RecordFactory } from '../../models/RecordFactory'
import { Taxcode } from '../../models/Taxcode'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { PDFPreviewModal } from '../common/PDFPreviewModal'
import { EstimateView } from './EstimateView'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { NewEstimatePage } from './NewEstimatePage'
import { NewEstimateCompletePage } from './NewEstimateCompletePage'

const viewWillAppear = async ({
    owner,
    urlParams,
    me,
    props: { action },
}: PocketComponent<{ action: 'new' | 'edit' | 'copy' }>) => {
    const items = await RecordFactory.getInstance().loadItems(owner.id ?? '')

    const defaultEstimate = await (async (): Promise<Estimate | null> => {
        switch (action) {
            case 'new': {
                const status =
                    !owner.bookingStatus || owner.bookingStatus === 'confirmed'
                        ? 'confirmed'
                        : 'sentQuote'
                const estimate = new Estimate({
                    owner,
                    vendor: owner,
                    date: moment(),
                    status,
                    confirm_status:
                        status === 'confirmed' || owner.bookingStatus === 'confirmed' ? '' : status,
                    channel: owner.channel,
                    memo: owner.estimatePolicy,
                    employee:
                        Feature.enabled(FEATURE.FEATURE_STAFFING, owner) &&
                        me.payee &&
                        me.perm === User.PERM_ROLE_EMPLOYEE
                            ? me
                            : undefined,
                    allow_surcharge: owner.sp && owner.allowSurcharge ? true : undefined,
                    ...(Taxcode.defaultTaxable(owner)
                        ? { nexus: 'pro', tax: owner.taxcode?.rate ?? 0, taxcode: owner.taxcode }
                        : {}),
                })
                estimate.syncSalesTax()
                return estimate
            }
            case 'edit': {
                if (!urlParams.id) {
                    return null
                }
                const estimate =
                    (await RecordFactory.getInstance().loadEstimate(urlParams.id)) ?? null
                if (!estimate) {
                    return null
                }

                estimate.syncSalesTax()
                return estimate
            }
            case 'copy': {
                if (!urlParams.id) {
                    return null
                }
                const estimate =
                    (await RecordFactory.getInstance().loadEstimate(urlParams.id)) ?? null
                if (!estimate) {
                    return null
                }

                return estimate.clone({ id: undefined })
            }
        }
    })()
    if (defaultEstimate === null) {
        return new RecordNotFoundError()
    }

    return { defaultEstimate, items }
}

const NewEstimateWorkflowRoot = ({
    data,
    base: { owner, logAndDisplayError },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    const { defaultEstimate, items } = data

    const isEditing = defaultEstimate.id !== undefined

    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams()

    const stepIndex = useMemo(() => {
        switch (searchParams.get('step')) {
            case 'review':
                return 1
            case 'success':
                return 2
            default:
                return 0
        }
    }, [searchParams])

    const [estimate, setEstimate] = useState(defaultEstimate)

    const [loadingButton, setLoadingButton] = useState<'save' | 'send' | null>(null)

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

    const onSave = async (shouldNotNotify: boolean) => {
        if (estimate.client === undefined) {
            return
        }

        setLoadingButton(shouldNotNotify ? 'save' : 'send')

        const result = await RecordFactory.getInstance().sendEstimate(estimate, shouldNotNotify)
        setLoadingButton(null)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }

        setEstimate(result.value)

        setSearchParams({ step: 'success' }, { replace: true })
    }

    const handleEstimateUpdate = (updatedEstimate: Estimate) => {
        if (updatedEstimate.taxcode !== undefined) {
            updatedEstimate.syncSalesTax()
        }
        if (updatedEstimate.lines.length > 0) {
            updatedEstimate.lines.forEach((line) => {
                line.trandoc = updatedEstimate
            })
            updatedEstimate.syncTotal()
        }
        setEstimate(updatedEstimate)
    }

    const handleShowPreview = async () => {
        if (estimate.client === undefined) {
            return
        }
        setIsPreviewModalOpen(true)
    }

    const canAdvanceToStep2 = estimate.client !== undefined

    WorkflowPage.useNavigationHandlers({
        stepIndex,
        canAdvanceToStep2,
        isEditing,
        recordID: estimate.id,
    })

    const hasUnsavedChanges = stepIndex <= 1 && BaseRecord.hasChanges(defaultEstimate, estimate)

    return (
        <>
            <WorkflowPage
                title={stepIndex === 0 ? 'Send estimate' : stepIndex === 1 ? 'Review' : undefined}
                backButtonTitle={
                    stepIndex === 1 ? (isEditing ? 'Edit Estimate' : 'Send Estimate') : undefined
                }
                onBack={() => navigate(-1)}
                steps={[isEditing ? 'Edit' : 'Create', 'Review', 'Send']}
                currentStepIndex={stepIndex}
                shouldForceCurrentStepCheckmark={stepIndex === 2}
                onClose={{
                    action: 'go-back',
                    excludePathname: /^\/estimates.*\/(new|edit|copy)/,
                    defaultPathname: '/dashboards/estimates',
                }}
                footerContent={
                    stepIndex === 0 || stepIndex === 1 ? (
                        <div className="flex w-full items-center justify-end gap-x-4">
                            {stepIndex === 0 ? (
                                <>
                                    <Button
                                        intent="secondary"
                                        disabled={estimate.client === undefined}
                                        onClick={handleShowPreview}
                                    >
                                        Preview
                                    </Button>

                                    <Button
                                        intent="primary"
                                        disabled={!canAdvanceToStep2}
                                        onClick={() => setSearchParams({ step: 'review' })}
                                    >
                                        Next
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        intent="secondary"
                                        disabled={estimate.client === undefined}
                                        onClick={handleShowPreview}
                                    >
                                        Preview
                                    </Button>

                                    <Button
                                        intent="secondary"
                                        onClick={() => onSave(true)}
                                        isLoading={loadingButton === 'save'}
                                    >
                                        Save
                                    </Button>

                                    {!!estimate.channel && (
                                        <Button
                                            intent="primary"
                                            onClick={() => onSave(false)}
                                            isLoading={loadingButton === 'send'}
                                        >
                                            Send
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    ) : undefined
                }
                shouldVerticallyCenterContent={stepIndex === 2}
            >
                {stepIndex === 0 ? (
                    <NewEstimatePage
                        estimate={estimate}
                        onUpdateEstimate={handleEstimateUpdate}
                        owner={owner}
                        items={items}
                    />
                ) : stepIndex === 1 && estimate.client !== undefined ? (
                    <div className="mt-6">
                        <EstimateView
                            estimate={estimate}
                            isWorkflowReviewStep
                            client={estimate.client}
                        />
                    </div>
                ) : stepIndex === 2 ? (
                    <NewEstimateCompletePage estimate={estimate} />
                ) : null}
            </WorkflowPage>

            <PDFPreviewModal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                endpoint={{ path: '/estimate/print', body: estimate.jsonObject() }}
            />

            <WorkflowPage.NavigationBlocker when={hasUnsavedChanges} />
        </>
    )
}

const render = (props: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    return (
        <WorkflowPage.ResetContainer>
            <NewEstimateWorkflowRoot {...props} />
        </WorkflowPage.ResetContainer>
    )
}

export const EstimateWorkflow = usePSComponent(render, viewWillAppear)
